import React, { useState } from 'react';
import styled from 'styled-components';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import ProductModal from '../components/ProductModal';

const Wishlist = () => {
  const { wishlistItems, removeFromWishlist, loading } = useWishlist();
  const { addToCart } = useCart();
  const { currentUser } = useAuth();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleRemoveFromWishlist = async (productId) => {
    await removeFromWishlist(productId);
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleAddToCartClick = (product, event) => {
    event.stopPropagation();
    setSelectedProduct({
      ...product,
      focusOnSize: true
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleAddToCart = (cartItem) => {
    addToCart({
      ...cartItem.product,
      size: cartItem.size,
      color: cartItem.color,
      quantity: cartItem.quantity
    });
    closeModal();
  };

  if (!currentUser) {
    return (
      <WishlistContainer>
        <EmptyState>
          <EmptyIcon>💝</EmptyIcon>
          <EmptyTitle>Please Log In</EmptyTitle>
          <EmptyText>You need to be logged in to view your wishlist.</EmptyText>
        </EmptyState>
      </WishlistContainer>
    );
  }

  if (loading) {
    return (
      <WishlistContainer>
        <LoadingState>
          <LoadingSpinner />
          <LoadingText>Loading your wishlist...</LoadingText>
        </LoadingState>
      </WishlistContainer>
    );
  }

  return (
    <>
      <SEO
        title="Wishlist - Nike 3D Store"
        description="View and manage your favorite Nike products. Shop your liked items and add them to cart."
        keywords="Nike wishlist, favorite products, liked items, Nike favorites"
        canonicalUrl="https://nike3d.com/wishlist"
      />
      <WishlistContainer>
        <WishlistHeader>
          <HeaderContent>
            <Title>My Wishlist</Title>
            <ItemCount>
              {wishlistItems.length} {wishlistItems.length === 1 ? 'item' : 'items'}
            </ItemCount>
          </HeaderContent>
        </WishlistHeader>

        {wishlistItems.length === 0 ? (
          <EmptyState>
            <EmptyIcon>💝</EmptyIcon>
            <EmptyTitle>Your wishlist is empty</EmptyTitle>
            <EmptyText>
              Start adding products to your wishlist by clicking the heart icon on any product.
            </EmptyText>
          </EmptyState>
        ) : (
          <ProductGrid>
            {wishlistItems.map(product => (
              <ProductCard 
                key={product.id} 
                onClick={() => handleProductClick(product)}
              >
                <ProductImageContainer>
                  <ProductImage src={product.image} alt={product.name} />
                  <ProductBadge>{product.badge}</ProductBadge>
                </ProductImageContainer>
                
                <ProductInfo>
                  <ProductHeader>
                    <ProductCategory>{product.category}</ProductCategory>
                    <ProductName>{product.name}</ProductName>
                  </ProductHeader>
                  
                  <ProductMeta>
                    <ProductRating>
                      <Stars rating={product.rating} />
                      <RatingText>({product.reviews})</RatingText>
                    </ProductRating>
                    <ProductPrice>${product.price}</ProductPrice>
                  </ProductMeta>
                  
                  <ProductActions>
                    <PrimaryActions>
                      <ActionButton 
                        primary 
                        onClick={(e) => handleAddToCartClick(product, e)}
                      >
                        <CartIcon>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="9" cy="21" r="1"></circle>
                            <circle cx="20" cy="21" r="1"></circle>
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                          </svg>
                        </CartIcon>
                        Add to Cart
                      </ActionButton>
                    </PrimaryActions>
                    <SecondaryActions>
                      <IconButton onClick={() => handleRemoveFromWishlist(product.id)} title="Remove from wishlist">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 6h18M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
                        </svg>
                      </IconButton>
                    </SecondaryActions>
                  </ProductActions>
                </ProductInfo>
              </ProductCard>
            ))}
          </ProductGrid>
        )}
        
        {selectedProduct && (
          <ProductModal
            product={selectedProduct}
            isOpen={isModalOpen}
            onClose={closeModal}
            onAddToCart={handleAddToCart}
          />
        )}
      </WishlistContainer>
    </>
  );
};

const Stars = ({ rating }) => {
  return (
    <StarsContainer>
      {[...Array(5)].map((_, i) => (
        <Star key={i} filled={i < Math.floor(rating)}>
          ★
        </Star>
      ))}
    </StarsContainer>
  );
};

export default Wishlist;

const WishlistContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 15px 10px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 5px;
  }
`;

const WishlistHeader = styled.div`
  background: linear-gradient(135deg, #111 0%, #333 100%);
  border-radius: 16px;
  padding: 40px;
  margin-bottom: 30px;
  color: white;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    text-align: center;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const ItemCount = styled.div`
  font-size: 18px;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ProductGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
    padding: 0 10px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    padding: 0 5px;
  }
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  }
  
  @media (max-width: 768px) {
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
    }
  }
`;

const ProductImageContainer = styled.div`
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  ${ProductCard}:hover & {
    transform: scale(1.05);
  }
`;

const ProductBadge = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
  background: #111;
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const ProductInfo = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    padding: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 10px;
  }
`;

const ProductHeader = styled.div`
  margin-bottom: 8px;
  
  @media (max-width: 480px) {
    margin-bottom: 6px;
  }
`;

const ProductMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    margin-bottom: 6px;
  }
  
  @media (max-width: 480px) {
    gap: 3px;
    margin-bottom: 4px;
  }
`;

const PrimaryActions = styled.div`
  display: flex;
  flex: 1;
`;

const SecondaryActions = styled.div`
  display: flex;
  align-items: center;
`;

const CartIcon = styled.div`
  display: flex;
  align-items: center;
  margin-right: 4px;
  
  @media (max-width: 480px) {
    margin-right: 3px;
    
    svg {
      width: 10px;
      height: 10px;
    }
  }
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6c757d;
  
  &:hover {
    background: #fee;
    border-color: #dc3545;
    color: #dc3545;
    transform: translateY(-1px);
  }
  
  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const ProductCategory = styled.div`
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
  font-weight: 500;
  
  @media (max-width: 480px) {
    font-size: 10px;
    margin-bottom: 2px;
  }
`;

const ProductName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111;
  margin: 0;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;



const ProductRating = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  
  @media (max-width: 480px) {
    gap: 3px;
  }
`;

const StarsContainer = styled.div`
  display: flex;
  gap: 1px;
`;

const Star = styled.span`
  color: ${props => props.filled ? '#ffc107' : '#e9ecef'};
  font-size: 12px;
  
  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const RatingText = styled.span`
  font-size: 11px;
  color: #888;
  
  @media (max-width: 480px) {
    font-size: 10px;
  }
`;

const ProductPrice = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #111;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
  
  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

const ProductActions = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  
  @media (max-width: 480px) {
    gap: 4px;
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border: 1px solid #111;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex: 1;
  
  @media (max-width: 480px) {
    padding: 6px 8px;
    font-size: 11px;
    border-radius: 4px;
  }
  
  ${props => props.primary ? `
    background: #111;
    color: white;
    
    &:hover {
      background: #000;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
  ` : `
    background: white;
    color: #111;
    
    &:hover {
      background: #111;
      color: white;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
  `}
`;



const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  text-align: center;
  max-width: 500px;
  margin: 0 auto;
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const EmptyTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #111;
  margin: 0 0 16px 0;
`;

const EmptyText = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin: 0;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e9ecef;
  border-top: 4px solid #111;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 18px;
  color: #666;
`;
